import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Tier } from "@/lib/pricing";
import { alertRef } from "@/lib/alertRef";

/**
 * Opens Stripe checkout for the given paid tier.
 * Single shared call site used by /pricing and CreditWallModal so both surfaces
 * stay on identical code path (no future drift).
 *
 * The edge function resolves the actual Stripe price ID server-side from
 * STRIPE_PRICE_LITE / STRIPE_PRICE_PRO env vars. Do not pass priceId from client.
 *
 * An existing subscriber does not go to Checkout at all. The function switches
 * the tier on their current Stripe subscription and returns { changed }, because
 * opening a second Checkout Session for someone who already subscribes creates a
 * second live subscription and bills them twice.
 *
 * alert_id: the job alert whose email link brought this reader, if any
 * (src/lib/alertRef.ts), so the subscription can be traced to the send.
 */
export async function openTierCheckout(tier: Exclude<Tier, "free">): Promise<void> {
  const { data, error } = await supabase.functions.invoke("create-job-seeker-checkout", {
    body: { tier, alert_id: alertRef() },
  });
  if (error) {
    toast.error((error as Error).message ?? "Could not start checkout");
    return;
  }

  // Existing subscriber: the plan changed in place, no Checkout involved.
  if (data?.changed === true) {
    const label = tier === "pro" ? "Pro" : "Lite";
    toast.success(
      data.direction === "downgrade"
        ? `Switched to ${label}. The credit is applied to your next invoice.`
        : `You are on ${label}. The difference has been charged, and it is live now.`,
    );
    // The webhook updates entitlements a moment later, so reload to pick them up.
    setTimeout(() => window.location.reload(), 1500);
    return;
  }
  if (data?.changed === false) {
    toast.info(data.message ?? "You are already on this plan");
    return;
  }

  if (!data?.url) {
    toast.error("Could not start checkout");
    return;
  }
  const url = data.url as string;
  // Break out of sandboxed iframes (Lovable preview) which block same-window
  // navigation to a cross-origin URL like checkout.stripe.com. Fall back to
  // a new tab, then same-window, then surface the URL via toast.
  try {
    if (window.top && window.top !== window.self) {
      window.top.location.href = url;
      return;
    }
  } catch {
    // cross-origin access to window.top is blocked — fall through
  }
  const opened = window.open(url, "_blank", "noopener,noreferrer");
  if (opened) return;
  try {
    window.location.href = url;
  } catch {
    toast.error("Could not open checkout. Copy this URL: " + url);
  }
}
